/**
 * Level6Scene - 关卡6：静电场
 * @description 教学目标：理解静电场对带电粒子的作用
 * 核心公式：F = qE（电场力 = 电荷量 × 电场强度）
 */

import Phaser from 'phaser';
import { Vec2 } from 'planck';
import type { LevelConfig } from '@/levels/LevelConfig.ts';
import { TutorialUI } from '@/ui/TutorialUI.ts';
import { getTutorial } from '@/data/tutorials.ts';
import { getGameState } from '@/core/GameState.ts';
import { EMField, type ChargedParticle } from '@/physics/EMField.ts';
import { FieldRenderer } from '@/physics/FieldRenderer.ts';

// 关卡6配置
interface Level6SpecificConfig {
  /** 场源电荷量 */
  sourceCharge: number;
  /** 场源位置 x */
  sourcePositionX: number;
  /** 场源位置 y */
  sourcePositionY: number;
  /** 粒子可选电荷量列表 */
  particleChargeOptions: number[];
  /** 粒子起始位置 x */
  particleStartX: number;
  /** 粒子起始位置 y */
  particleStartY: number;
  /** 目标位置 x */
  targetX: number;
  /** 目标位置 y */
  targetY: number;
  /** 目标半径 */
  targetRadius: number;
  /** 公式 */
  formula: string;
  /** 提示 */
  hint: string;
}

// 游戏状态
type GameState = 'idle' | 'running' | 'success' | 'failed';

// 游戏尺寸常量
const GAME_WIDTH = 800;
const GAME_HEIGHT = 600;

/**
 * Level6Scene 类
 * 关卡6场景，实现静电场教学
 */
export class Level6Scene extends Phaser.Scene {
  // 关卡配置
  private levelConfig: LevelConfig | null = null;
  private levelSpecific: Level6SpecificConfig | null = null;

  // 游戏状态
  private gameState: GameState = 'idle';
  private elapsedTime: number = 0;

  // 电磁场模块
  private emField: EMField;
  private fieldRenderer: FieldRenderer;

  // 带电粒子
  private particle: ChargedParticle | null = null;
  private selectedCharge: number = 0;

  // 游戏对象
  private targetZone: Phaser.GameObjects.Graphics | null = null;
  private trajectoryLine: Phaser.GameObjects.Graphics | null = null;

  // UI 元素
  private hintText: Phaser.GameObjects.Text | null = null;
  private chargeButtons: Phaser.GameObjects.Graphics[] = [];
  private startButton: Phaser.GameObjects.Graphics | null = null;
  private resultText: Phaser.GameObjects.Text | null = null;

  // 教程组件
  private tutorialUI: TutorialUI | null = null;

  constructor() {
    super({ key: 'Level6Scene' });
    // 初始化电磁场（使用较大的缩放因子使效果更明显）
    this.emField = new EMField(1e-4);
    this.fieldRenderer = null as unknown as FieldRenderer; // 将在 create 中初始化
  }

  preload(): void {
    // 加载关卡配置
    this.load.json('level-6', 'src/levels/levels/level-6.json');
  }

  create(): void {
    // 加载关卡配置
    this.levelConfig = this.cache.json.get('level-6') as LevelConfig;

    if (this.levelConfig && this.levelConfig.levelSpecific) {
      this.levelSpecific = this.levelConfig.levelSpecific as unknown as Level6SpecificConfig;
    }

    // 初始化场渲染器
    this.fieldRenderer = new FieldRenderer(this, this.emField, {}, GAME_HEIGHT, 1);

    // 创建背景
    this.createBackground();

    // 设置电场源
    this.setupElectricField();

    // 创建游戏场景
    this.createGameScene();

    // 创建 UI
    this.createUI();

    // 设置输入事件
    this.setupInputEvents();

    // 显示教程
    this.showTutorial();

    // 渲染场线
    this.fieldRenderer.render();
  }

  override update(_time: number, delta: number): void {
    if (this.gameState !== 'running' || !this.particle) return;

    // 更新时间
    this.elapsedTime += delta / 1000;

    // 更新粒子运动（使用电磁场计算）
    const dt = Math.min(delta / 1000, 0.02); // 限制最大时间步长，避免数值不稳定
    this.particle = this.emField.updateParticleMotion(this.particle, dt);

    // 渲染粒子
    this.renderParticle();

    // 绘制轨迹
    this.drawTrajectory();

    // 检测胜利/失败
    this.checkGameEnd();
  }

  /**
   * 创建背景
   */
  private createBackground(): void {
    // 深色背景
    this.cameras.main.setBackgroundColor('#1a1a2e');

    // 添加网格线作为参考
    const gridGraphics = this.add.graphics();
    gridGraphics.lineStyle(1, 0x2a2a4a, 0.3);

    // 垂直网格线
    for (let x = 0; x <= GAME_WIDTH; x += 50) {
      gridGraphics.beginPath();
      gridGraphics.moveTo(x, 0);
      gridGraphics.lineTo(x, GAME_HEIGHT);
      gridGraphics.strokePath();
    }

    // 水平网格线
    for (let y = 0; y <= GAME_HEIGHT; y += 50) {
      gridGraphics.beginPath();
      gridGraphics.moveTo(0, y);
      gridGraphics.lineTo(GAME_WIDTH, y);
      gridGraphics.strokePath();
    }
  }

  /**
   * 设置电场源
   */
  private setupElectricField(): void {
    if (!this.levelSpecific) return;

    // 添加正电荷场源
    this.emField.addChargeSource({
      id: 'source-1',
      position: Vec2(
        this.levelSpecific.sourcePositionX,
        this.levelSpecific.sourcePositionY
      ),
      charge: this.levelSpecific.sourceCharge,
    });
  }

  /**
   * 创建游戏场景
   */
  private createGameScene(): void {
    if (!this.levelSpecific) return;

    // 目标区域
    this.targetZone = this.add.graphics();
    this.targetZone.fillStyle(0x4ade80, 0.3);
    this.targetZone.fillCircle(
      this.levelSpecific.targetX,
      GAME_HEIGHT - this.levelSpecific.targetY,
      this.levelSpecific.targetRadius
    );
    this.targetZone.lineStyle(3, 0x4ade80);
    this.targetZone.strokeCircle(
      this.levelSpecific.targetX,
      GAME_HEIGHT - this.levelSpecific.targetY,
      this.levelSpecific.targetRadius
    );

    // 目标标签
    this.add.text(
      this.levelSpecific.targetX,
      GAME_HEIGHT - this.levelSpecific.targetY + this.levelSpecific.targetRadius + 20,
      '目标区域',
      {
        fontSize: '14px',
        color: '#4ade80',
        fontFamily: 'Arial',
      }
    ).setOrigin(0.5);

    // 粒子起始位置标记
    const startMarker = this.add.graphics();
    startMarker.lineStyle(2, 0xffa500);
    startMarker.strokeCircle(
      this.levelSpecific.particleStartX,
      GAME_HEIGHT - this.levelSpecific.particleStartY,
      15
    );

    this.add.text(
      this.levelSpecific.particleStartX,
      GAME_HEIGHT - this.levelSpecific.particleStartY - 25,
      '起点',
      {
        fontSize: '12px',
        color: '#ffa500',
        fontFamily: 'Arial',
      }
    ).setOrigin(0.5);

    // 场源位置说明
    this.add.text(
      this.levelSpecific.sourcePositionX,
      GAME_HEIGHT - this.levelSpecific.sourcePositionY + 35,
      `电荷 Q=${this.levelSpecific.sourceCharge > 0 ? '+' : ''}${this.levelSpecific.sourceCharge}`,
      {
        fontSize: '14px',
        color: '#ffffff',
        fontFamily: 'Arial',
      }
    ).setOrigin(0.5);

    // 初始化轨迹线
    this.trajectoryLine = this.add.graphics();
    this.trajectoryLine.setDepth(4);
  }

  /**
   * 创建 UI
   */
  private createUI(): void {
    // 公式显示
    this.add.text(GAME_WIDTH / 2, 50, '电场力公式：F = qE', {
      fontSize: '24px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    // 提示文本
    this.hintText = this.add.text(
      GAME_WIDTH / 2,
      85,
      this.levelSpecific?.hint ?? '选择粒子电荷，观察电场力作用',
      {
        fontSize: '16px',
        color: '#888888',
        fontFamily: 'Arial',
      }
    ).setOrigin(0.5);

    // 电荷选择按钮
    this.createChargeButtons();

    // 开始按钮
    this.createStartButton();

    // 结果文本（初始隐藏）
    this.resultText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, '', {
      fontSize: '28px',
      color: '#4ade80',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5).setVisible(false);

    // 物理说明区域
    this.createPhysicsInfo();
  }

  /**
   * 创建电荷选择按钮
   */
  private createChargeButtons(): void {
    if (!this.levelSpecific) return;

    const charges = this.levelSpecific.particleChargeOptions;
    const startX = GAME_WIDTH / 2 - (charges.length * 70) / 2;
    const y = 520;

    this.add.text(GAME_WIDTH / 2, y - 30, '选择粒子电荷 q:', {
      fontSize: '14px',
      color: '#888888',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    charges.forEach((charge, i) => {
      const x = startX + i * 70 + 35;
      const btn = this.add.graphics();
      const isSelected = this.selectedCharge === charge;
      const color = charge > 0 ? 0xe74c3c : 0x3498db;

      btn.fillStyle(isSelected ? color : 0x4a4a6a, isSelected ? 1 : 0.8);
      btn.fillRect(x - 30, y, 60, 40);
      btn.lineStyle(2, isSelected ? color : 0x6a6a8a);
      btn.strokeRect(x - 30, y, 60, 40);

      this.add.text(x, y + 20, `${charge > 0 ? '+' : ''}${charge}`, {
        fontSize: '16px',
        color: '#ffffff',
        fontFamily: 'Arial',
        fontStyle: 'bold',
      }).setOrigin(0.5);

      btn.setInteractive(
        new Phaser.Geom.Rectangle(x - 30, y, 60, 40),
        Phaser.Geom.Rectangle.Contains
      );

      btn.on('pointerdown', () => {
        if (this.gameState === 'idle') {
          this.selectedCharge = charge;
          this.updateChargeButtons();
          this.updateHint();
        }
      });

      btn.on('pointerover', () => {
        if (this.gameState === 'idle') {
          btn.clear();
          btn.fillStyle(charge > 0 ? 0xe74c3c : 0x3498db, 0.9);
          btn.fillRect(x - 30, y, 60, 40);
          btn.strokeRect(x - 30, y, 60, 40);
        }
      });

      btn.on('pointerout', () => {
        if (this.gameState === 'idle') {
          this.updateChargeButtons();
        }
      });

      this.chargeButtons.push(btn);
    });
  }

  /**
   * 更新电荷按钮状态
   */
  private updateChargeButtons(): void {
    if (!this.levelSpecific) return;

    const charges = this.levelSpecific.particleChargeOptions;
    const startX = GAME_WIDTH / 2 - (charges.length * 70) / 2;
    const y = 520;

    this.chargeButtons.forEach((btn, i) => {
      const charge = charges[i];
      if (charge === undefined) return;
      const x = startX + i * 70 + 35;
      const isSelected = this.selectedCharge === charge;
      const color = charge > 0 ? 0xe74c3c : 0x3498db;

      btn.clear();
      btn.fillStyle(isSelected ? color : 0x4a4a6a, isSelected ? 1 : 0.8);
      btn.fillRect(x - 30, y, 60, 40);
      btn.lineStyle(2, isSelected ? color : 0x6a6a8a);
      btn.strokeRect(x - 30, y, 60, 40);
    });
  }

  /**
   * 更新提示文本
   */
  private updateHint(): void {
    if (!this.levelSpecific || this.selectedCharge === 0) return;

    const particlePos = Vec2(this.levelSpecific.particleStartX, this.levelSpecific.particleStartY);
    const field = this.emField.calculateElectricField(particlePos);

    // 计算电场力 F = qE
    const forceMagnitude = Math.abs(this.selectedCharge * field.magnitude);

    // 根据电荷正负判断力的方向
    const direction = this.selectedCharge > 0 ? '远离正电荷' : '向正电荷移动';

    this.hintText?.setText(
      `电荷 q=${this.selectedCharge} → 电场力 F≈${forceMagnitude.toFixed(1)}，粒子将${direction}`
    );
  }

  /**
   * 创建物理信息显示
   */
  private createPhysicsInfo(): void {
    // 显示场强公式
    this.add.text(50, GAME_HEIGHT - 50, 'E = kQ/r² （电场强度）', {
      fontSize: '12px',
      color: '#888888',
      fontFamily: 'Arial',
    });

    // 显示力公式
    this.add.text(200, GAME_HEIGHT - 50, 'F = qE （电场力）', {
      fontSize: '12px',
      color: '#888888',
      fontFamily: 'Arial',
    });

    // 显示运动公式
    this.add.text(350, GAME_HEIGHT - 50, 'a = F/m （加速度）', {
      fontSize: '12px',
      color: '#888888',
      fontFamily: 'Arial',
    });
  }

  /**
   * 创建开始按钮
   */
  private createStartButton(): void {
    this.startButton = this.add.graphics();
    this.startButton.fillStyle(0x4ade80, 1);
    this.startButton.fillRect(GAME_WIDTH - 140, 520, 120, 50);
    this.startButton.lineStyle(2, 0x4ade80);
    this.startButton.strokeRect(GAME_WIDTH - 140, 520, 120, 50);

    this.add.text(GAME_WIDTH - 80, 545, '开始', {
      fontSize: '20px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    this.startButton.setInteractive(
      new Phaser.Geom.Rectangle(GAME_WIDTH - 140, 520, 120, 50),
      Phaser.Geom.Rectangle.Contains
    );
    this.startButton.on('pointerdown', () => this.startGame());

    this.startButton.on('pointerover', () => {
      this.startButton?.clear();
      this.startButton?.fillStyle(0x5aee90, 1);
      this.startButton?.fillRect(GAME_WIDTH - 140, 520, 120, 50);
      this.startButton?.strokeRect(GAME_WIDTH - 140, 520, 120, 50);
    });

    this.startButton.on('pointerout', () => {
      this.startButton?.clear();
      this.startButton?.fillStyle(0x4ade80, 1);
      this.startButton?.fillRect(GAME_WIDTH - 140, 520, 120, 50);
      this.startButton?.strokeRect(GAME_WIDTH - 140, 520, 120, 50);
    });
  }

  /**
   * 设置输入事件
   */
  private setupInputEvents(): void {
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (this.gameState !== 'idle') return;

      if (event.key === 'Enter') {
        this.startGame();
      }

      if (event.key === 'r' || event.key === 'R') {
        this.resetGame();
      }
    });
  }

  /**
   * 渲染带电粒子
   */
  private renderParticle(): void {
    if (!this.particle) return;

    // 使用场渲染器渲染粒子
    this.fieldRenderer.renderParticle(this.particle.id, this.particle.position, this.particle.charge);
  }

  /**
   * 绘制运动轨迹
   */
  private drawTrajectory(): void {
    if (!this.particle || !this.trajectoryLine) return;

    const screenX = this.particle.position.x;
    const screenY = GAME_HEIGHT - this.particle.position.y;

    // 使用点来绘制轨迹（每帧添加一个点）
    const color = this.particle.charge > 0 ? 0xff6b6b : 0x4ecdc4;
    this.trajectoryLine.fillStyle(color, 0.5);
    this.trajectoryLine.fillCircle(screenX, screenY, 3);
  }

  /**
   * 开始游戏
   */
  private startGame(): void {
    if (this.gameState !== 'idle') return;
    if (this.selectedCharge === 0) {
      this.hintText?.setText('请先选择粒子电荷！');
      return;
    }

    if (!this.levelSpecific) return;

    this.gameState = 'running';
    this.elapsedTime = 0;

    // 创建带电粒子
    this.particle = {
      id: 'particle-1',
      position: Vec2(this.levelSpecific.particleStartX, this.levelSpecific.particleStartY),
      velocity: Vec2(0, 0), // 初始静止
      charge: this.selectedCharge,
      mass: 1.0, // 简化质量
    };

    // 清除轨迹
    if (this.trajectoryLine) {
      this.trajectoryLine.clear();
    }

    // 隐藏结果
    this.resultText?.setVisible(false);

    // 更新开始按钮状态
    this.updateStartButtonState('running');

    // 更新提示
    this.hintText?.setText('观察粒子在电场中的运动...');
  }

  /**
   * 更新开始按钮状态
   */
  private updateStartButtonState(state: GameState): void {
    if (!this.startButton) return;

    this.startButton.clear();

    if (state === 'running') {
      this.startButton.fillStyle(0x6a6a8a, 1);
    } else {
      this.startButton.fillStyle(0x4ade80, 1);
    }
    this.startButton.fillRect(GAME_WIDTH - 140, 520, 120, 50);
    this.startButton.strokeRect(GAME_WIDTH - 140, 520, 120, 50);
  }

  /**
   * 检测游戏结束
   */
  private checkGameEnd(): void {
    if (!this.particle || !this.levelSpecific) return;

    const targetX = this.levelSpecific.targetX;
    const targetY = this.levelSpecific.targetY;
    const targetRadius = this.levelSpecific.targetRadius;

    // 计算粒子与目标的距离
    const dx = this.particle.position.x - targetX;
    const dy = this.particle.position.y - targetY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // 检测是否到达目标
    if (distance <= targetRadius) {
      this.gameState = 'success';
      this.showSuccessResult();
      this.updateStartButtonState('idle');
      return;
    }

    // 检测是否超出边界
    if (
      this.particle.position.x < 0 ||
      this.particle.position.x > GAME_WIDTH ||
      this.particle.position.y < 0 ||
      this.particle.position.y > GAME_HEIGHT
    ) {
      this.gameState = 'failed';
      this.showFailResult('粒子超出边界！');
      this.updateStartButtonState('idle');
    }

    // 检测是否接近场源（会被场源"吸收"）
    const sourceDx = this.particle.position.x - this.levelSpecific.sourcePositionX;
    const sourceDy = this.particle.position.y - this.levelSpecific.sourcePositionY;
    const sourceDistance = Math.sqrt(sourceDx * sourceDx + sourceDy * sourceDy);

    if (sourceDistance < 25) {
      this.gameState = 'failed';
      this.showFailResult('粒子被场源吸收！');
      this.updateStartButtonState('idle');
    }
  }

  /**
   * 显示成功结果
   */
  private showSuccessResult(): void {
    const gameState = getGameState();
    gameState.completeLevel(6, this.elapsedTime);

    this.resultText?.setText(`🎉 成功！用时 ${this.elapsedTime.toFixed(2)}秒`)
      .setColor('#4ade80')
      .setVisible(true);

    this.hintText?.setText(`电场力 F=qE 使粒子正确到达目标！`);

    this.createCompletionButtons();
  }

  /**
   * 创建完成后的按钮
   */
  private createCompletionButtons(): void {
    const y = GAME_HEIGHT / 2 + 60;

    const nextBtn = this.add.text(GAME_WIDTH / 2 - 80, y, '下一关', {
      fontSize: '18px',
      color: '#4ade80',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 15, y: 8 },
    }).setOrigin(0.5).setInteractive();

    nextBtn.on('pointerdown', () => {
      const gameState = getGameState();
      if (gameState.isLevelUnlocked(7)) {
        this.scene.start('Level7Scene');
      } else {
        this.scene.start('LevelSelectScene');
      }
    });

    const backBtn = this.add.text(GAME_WIDTH / 2 + 80, y, '返回', {
      fontSize: '18px',
      color: '#888888',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 15, y: 8 },
    }).setOrigin(0.5).setInteractive();

    backBtn.on('pointerdown', () => {
      this.scene.start('LevelSelectScene');
    });
  }

  /**
   * 显示失败结果
   */
  private showFailResult(reason: string): void {
    this.resultText?.setText(`❌ ${reason}`)
      .setColor('#ff6b6b')
      .setVisible(true);

    // 根据失败原因给出提示
    if (reason.includes('边界')) {
      this.hintText?.setText('提示：负电荷粒子会被正电荷吸引，正电荷粒子会被排斥');
    } else if (reason.includes('吸收')) {
      this.hintText?.setText('提示：负电荷粒子向正电荷移动，小心不要被吸收！');
    }
  }

  /**
   * 重置游戏
   */
  private resetGame(): void {
    this.gameState = 'idle';
    this.elapsedTime = 0;
    this.particle = null;

    // 清除轨迹
    if (this.trajectoryLine) {
      this.trajectoryLine.clear();
    }

    // 清除粒子渲染
    this.fieldRenderer.removeParticleRender('particle-1');

    // 隐藏结果
    this.resultText?.setVisible(false);

    // 重置提示
    this.hintText?.setText(this.levelSpecific?.hint ?? '选择粒子电荷，观察电场力作用');

    // 更新按钮状态
    this.updateStartButtonState('idle');
  }

  /**
   * 显示教程
   */
  private showTutorial(): void {
    const tutorialData = getTutorial('level-6');
    if (!tutorialData) return;

    this.tutorialUI = new TutorialUI(this, {
      levelNumber: 6,
      tutorialData,
      onStart: () => {
        this.tutorialUI?.destroy();
        this.tutorialUI = null;
      },
      onSkip: () => {
        this.tutorialUI?.destroy();
        this.tutorialUI = null;
      },
    });
  }

  /**
   * 获取当前游戏状态
   */
  getGameState(): GameState {
    return this.gameState;
  }

  /**
   * 获取当前时间
   */
  getElapsedTime(): number {
    return this.elapsedTime;
  }

  /**
   * 获取粒子位置
   */
  getParticlePosition(): { x: number; y: number } | null {
    return this.particle ? { x: this.particle.position.x, y: this.particle.position.y } : null;
  }

  /**
   * 获取选中的电荷
   */
  getSelectedCharge(): number {
    return this.selectedCharge;
  }
}